import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GoalCardMultiSelectContextMenu } from 'shared/components/connected/goal-card-multi-select-context-menu';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useOpenCreateGoalDialog } from 'shared/contexts/goal-form';
import { Goal, UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { InheritColors } from 'shared/types/inherit-colors';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';

import { ARCHIVED_SECTION_ID, COMPLETED_SECTION_ID } from '../../constants';
import { useContextMenu } from '../../context-menu-context';
import { useLifeAreaGoalListTree } from '../../hooks/use-life-area-goal-list-tree';
import { GroupingGoalListProps } from '../../types';
import { LifeAreaHeader } from '../life-area-header';
import { TreeGoalCard } from '../tree-goal-card';
import * as Styled from './life-area-grouping-goal-list.style';

const droppedTypes = {
  [TaskListItemTypes.Root]: [
    TaskListItemTypes.Goal,
    TaskListItemTypes.LifeArea,
  ],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.LifeArea]: [TaskListItemTypes.Goal],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

const dragTypes = [TaskListItemTypes.Goal];

export const LifeAreaGroupingGoalList: React.FC<GroupingGoalListProps> = ({
  goals,
  collapsedGoals,
  onGoalCollapse,
  scrollElement,
  onReorder,
  onUpdateProgress,
  hideArchived,
  hideCompleted,
  isMultiSelectModifierPressed,
  excludedContextMenuOptions,
}) => {
  const { t } = useTranslation();
  const lifeAreas = useSortedLocalizedLifeAreas();

  const goalsMap = useMemo(
    () =>
      goals.reduce<Record<ID, Goal>>((acc, goal) => {
        acc[goal.id] = goal;
        return acc;
      }, {}),
    [goals],
  );

  const lifeAreasMap = useMemo(
    () =>
      lifeAreas.reduce<Record<ID, LifeArea>>((acc, lifeArea) => {
        acc[lifeArea.id] = lifeArea;
        return acc;
      }, {}),
    [lifeAreas],
  );

  const treeItems = useLifeAreaGoalListTree(goals, lifeAreas, {
    hideArchived,
    hideCompleted,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: UpdateGoal[] = [];
    const updatedSections: { id: ID; goalSorting: ID[] }[] = [];

    // Collect section updates
    updatedItems.forEach((item) => {
      if (item.type === TaskListItemTypes.Goal) {
        if (!goalsMap[item.id]) {
          return;
        }
        const goalToUpdate: UpdateGoal = { id: item.id };
        const parentItem =
          updatedItems.find(({ children }) => children?.includes(item.id)) ??
          Object.values(treeItems).find(({ children }) =>
            children?.includes(item.id),
          );

        const parentGoal = parentItem ? goalsMap[parentItem.id] : undefined;

        // set goal sorting to the order of the current children
        goalToUpdate.goalSorting = item.children ?? null;

        if (parentGoal) {
          // update goal parentIds and lifeAreaId
          goalToUpdate.parentIds = [
            ...(parentGoal.parentIds ?? []),
            parentGoal.id,
          ];
          goalToUpdate.lifeAreaId = null;
          goalToUpdate.color =
            goalsMap[item.id].color === InheritColors.LifeArea
              ? InheritColors.Goal
              : goalsMap[item.id].color;
          updatedDocs.push(goalToUpdate);
          return;
        }

        const parentLifeArea = parentItem
          ? lifeAreasMap[parentItem.id]
          : undefined;

        goalToUpdate.parentIds = null;
        goalToUpdate.lifeAreaId =
          !parentLifeArea || parentLifeArea.id === lifeAreaOtherID
            ? null
            : parentLifeArea.id;
        goalToUpdate.color =
          goalsMap[item.id].color === InheritColors.Goal
            ? InheritColors.LifeArea
            : goalsMap[item.id].color;

        updatedDocs.push(goalToUpdate);
      }

      if (item.type === TaskListItemTypes.LifeArea) {
        updatedSections.push({ id: item.id, goalSorting: item.children ?? [] });
      }

      if (item.type === TaskListItemTypes.Root) {
        updatedSections.push({
          id: rootId,
          goalSorting: item.children ?? [],
        });
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  const { close, position, payload } = useContextMenu();
  const openCreateGoalDialog = useOpenCreateGoalDialog();

  const renderItem = useCallback(
    ({ item, itemInstance, tree }: RenderItemProps) => {
      const itemId = itemInstance.getItemData().sourceId;
      const itemData = treeItems[itemId];

      if (!itemData) {
        return null;
      }

      if (itemData.type === TaskListItemTypes.Goal) {
        const goal = goalsMap[itemId];
        if (!goal) {
          return null;
        }

        return (
          <TreeGoalCard
            item={itemInstance}
            goal={goal}
            tree={tree}
            onUpdateProgress={onUpdateProgress}
            hideLifeArea
            isMultiSelectModifierPressed={isMultiSelectModifierPressed}
            excludedContextMenuOptions={excludedContextMenuOptions}
          />
        );
      }

      if (item.type === TaskListItemTypes.LifeArea) {
        const rootChildren = treeItems.root.children ?? [];
        const isFirst = rootChildren[0] === itemId;

        if (itemId === COMPLETED_SECTION_ID) {
          return (
            <LifeAreaHeader
              item={itemInstance}
              lifeArea={undefined}
              overrideTitle={t('pages.goals.completed.title')}
              overrideIcon={null}
              disableLink
              useSecondaryTitle
              isFirst={isFirst}
            />
          );
        }
        if (itemId === ARCHIVED_SECTION_ID) {
          return (
            <LifeAreaHeader
              item={itemInstance}
              lifeArea={undefined}
              overrideTitle={t('pages.goals.archived.title')}
              overrideIcon={null}
              disableLink
              useSecondaryTitle
              isFirst={isFirst}
            />
          );
        }

        const lifeArea = lifeAreasMap[itemId];
        const handleAddGoal = () =>
          openCreateGoalDialog({
            lifeAreaId: lifeArea?.id,
          });

        return (
          <LifeAreaHeader
            item={itemInstance}
            lifeArea={lifeArea}
            isFirst={isFirst}
            onAddGoal={handleAddGoal}
          />
        );
      }

      return null;
    },
    [
      treeItems,
      goalsMap,
      onUpdateProgress,
      isMultiSelectModifierPressed,
      excludedContextMenuOptions,
      lifeAreasMap,
      t,
      openCreateGoalDialog,
    ],
  );

  if (!goals.length) {
    return null;
  }

  return (
    <Styled.Container>
      <VirtualizedTreeList
        items={treeItems}
        rootItemId={rootId}
        renderItem={renderItem}
        scrollElement={scrollElement}
        collapsedItems={collapsedGoals}
        onCollapse={onGoalCollapse}
        onReorder={_onReorder}
        dropTypes={droppedTypes}
        dragTypes={dragTypes}
        indentationWidth={2.5}
      />
      {position && payload && (
        <GoalCardMultiSelectContextMenu
          goals={payload.goals}
          location={position}
          onClose={close}
          initialActiveSubMenu={payload.initialActiveSubMenu}
        />
      )}
    </Styled.Container>
  );
};
